import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { AI_CREDITS, PRACTICE_LIMITS, type CurriculumStandard, type PaperLanguage } from "@jomcerdik/shared";
import { formatMYR } from "../lib/format";
import { Alert, Button } from "../components/Form";
import { api, errorMessage, waitForExam } from "../lib/api";
import { locale, t, tk } from "../lib/i18n";

type Version = {
  versionId: string;
  framework: string;
  level: "rendah" | "menengah";
  year: number;
  subject: string;
  subjectName: string;
  stream: string;
  language: "ms" | "en";
  standards: number;
  languages: PaperLanguage[];
};

const LANG_LABEL: Record<PaperLanguage, string> = { ms: "Bahasa Melayu", en: "English", dlp: "DLP (BM + English)" };
const levelName = (l: string, y: number) => (l === "menengah" ? t("Tingkatan {n}", { n: y }) : t("Tahun {n}", { n: y }));
const variantName = (v: Version) =>
  [v.stream === "SK" ? "SK" : v.stream === "SJK" ? "SJK" : null, v.language === "en" ? t("versi English") : null].filter(Boolean).join(" · ") || t("Standard");

const FAIL: Record<string, string> = {
  validation_failed: tk("Kertas belum lulus semakan kandungan atau tulisan. Kredit anda tidak ditolak."),
  image_generation_failed: tk("Gambar belum berjaya dijana. Kertas belum disiapkan dan kredit anda tidak ditolak."),
  provider_error: tk("Perkhidmatan AI tidak dapat dihubungi. Kredit anda tidak ditolak."),
  internal_error: tk("Ralat sistem. Kredit anda tidak ditolak."),
};

/** Practice-paper generator driven by the imported DSKP (subject → level/year → SK/SP). */
/**
 * Paper balance, shown big above both generator tabs: how many more papers this user can generate
 * (1 credit = 1 paper). Turns amber when 5 or fewer are left and red at 0, with the top-up offer.
 */
export function CreditLine({ credits, unlimited }: { credits: number | null; unlimited: boolean }) {
  if (unlimited) return <span className="text-sm text-muted">Admin: <strong>{t("tanpa had")}</strong></span>;
  if (credits === null) return null;
  const tone = credits <= 0 ? "border-red-300 bg-red-50 text-red-800" : credits <= 5 ? "border-amber-300 bg-amber-50 text-amber-900" : "border-accent bg-accent-soft/40 text-brand-ink";
  return (
    <span className={`flex flex-wrap items-center gap-x-4 gap-y-2 rounded-card border-2 p-3 ${tone}`}>
      <span className="flex items-baseline gap-2">
        <span className="text-4xl font-black tabular-nums">{credits.toLocaleString(locale())}</span>
        <span className="text-sm font-semibold">{t("kertas lagi boleh dijana")}</span>
      </span>
      <span className="flex-1 text-xs">
        {credits <= 0 ? t("Kertas AI anda sudah habis.") : credits <= 5 ? t("Hampir habis!") : ""}{" "}
        {t("Tambah {n} kertas dengan {price}.", { n: AI_CREDITS.TOPUP_CREDITS, price: formatMYR(AI_CREDITS.TOPUP_PRICE_SEN) })}
      </span>
      <Link to="/kredit" className="rounded-full bg-brand px-4 py-1.5 text-sm font-bold text-white">{t("Tambah kertas")}</Link>
    </span>
  );
}

export function DskpGenerator({ credits, unlimited, onDone }: { credits: number | null; unlimited: boolean; onDone: () => void }) {
  const [versions, setVersions] = useState<Version[] | null>(null);
  const [subject, setSubject] = useState<string>("");
  const [levelYear, setLevelYear] = useState<string>("");
  const [versionId, setVersionId] = useState<string>("");
  const [standards, setStandards] = useState<CurriculumStandard[]>([]);
  const [picked, setPicked] = useState<string[]>([]);
  const [count, setCount] = useState(10);
  const [language, setLanguage] = useState<PaperLanguage>("ms");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    api<{ versions: Version[] }>("/api/exams/catalogue").then((r) => setVersions(r.versions)).catch((e) => setError(errorMessage(e)));
  }, []);

  const subjects = useMemo(() => {
    const m = new Map<string, string>();
    for (const v of versions ?? []) m.set(v.subject, v.subjectName);
    return [...m.entries()];
  }, [versions]);
  const levels = useMemo(() => {
    const m = new Map<string, string>();
    for (const v of versions ?? []) if (v.subject === subject) m.set(`${v.level}:${v.year}`, levelName(v.level, v.year));
    return [...m.entries()];
  }, [versions, subject]);
  const variants = useMemo(() => (versions ?? []).filter((v) => v.subject === subject && `${v.level}:${v.year}` === levelYear), [versions, subject, levelYear]);
  const version = variants.find((v) => v.versionId === versionId);

  // Auto-select single choices.
  useEffect(() => { if (levels.length && !levels.some(([k]) => k === levelYear)) setLevelYear(levels[0]![0]); }, [levels, levelYear]);
  useEffect(() => { if (variants.length && !variants.some((v) => v.versionId === versionId)) setVersionId(variants[0]!.versionId); }, [variants, versionId]);
  useEffect(() => {
    setPicked([]);
    setStandards([]);
    if (!versionId) return;
    api<{ standards: CurriculumStandard[] }>(`/api/exams/standards/${versionId}`).then((r) => setStandards(r.standards)).catch((e) => setError(errorMessage(e)));
  }, [versionId]);
  useEffect(() => {
    if (!version) return;
    const langs = version.language === "en" ? (["en"] as PaperLanguage[]) : version.languages;
    if (!langs.includes(language)) setLanguage(langs[0]!);
  }, [version, language]);

  const groups = useMemo(() => {
    const m = new Map<string, CurriculumStandard[]>();
    for (const s of standards) {
      const k = s.topic ?? s.skDesc ?? s.skCode;
      m.set(k, [...(m.get(k) ?? []), s]);
    }
    return [...m.entries()];
  }, [standards]);

  const full = picked.length >= PRACTICE_LIMITS.MAX_STANDARDS;
  const toggle = (code: string) => setPicked((cur) => (cur.includes(code) ? cur.filter((x) => x !== code) : full ? cur : [...cur, code]));
  const toggleGroup = (codes: string[]) =>
    setPicked((cur) => {
      const all = codes.every((c) => cur.includes(c));
      if (all) return cur.filter((c) => !codes.includes(c));
      const next = [...cur];
      for (const c of codes) if (!next.includes(c) && next.length < PRACTICE_LIMITS.MAX_STANDARDS) next.push(c);
      return next;
    });

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const r = await api<{ id: string; status: string; failure?: string }>("/api/exams/generate", {
        method: "POST",
        json: { mode: "dskp", versionId, spCodes: picked, questionCount: count, language },
      });
      let done: { status: string; failure?: string } = r;
      if (r.status === "generating") {
        onDone();
        done = await waitForExam(r.id);
      }
      if (done.status === "ready") setNotice(t("Kertas siap! Muat turun di senarai 'Kertas saya'."));
      else if (done.status === "generating") setNotice(t("Masih dijana. Anda boleh tinggalkan halaman ini; kertas akan muncul dalam senarai."));
      else setError(t(FAIL[done.failure ?? ""] ?? tk("Penjanaan gagal.")));
      onDone();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (versions === null) return <p className="text-sm text-muted">{t("Memuatkan DSKP…")}</p>;
  if (versions.length === 0) return <Alert kind="info">{t("Data DSKP sedang dimuatkan ke sistem. Cuba lagi sebentar lagi.")}</Alert>;

  const langs: PaperLanguage[] = version ? (version.language === "en" ? ["en"] : version.languages) : [];

  return (
    <form onSubmit={submit} className="space-y-4 rounded-card border border-surface-line p-4">
      <h2 className="text-lg font-semibold">{t("Latihan ikut DSKP")}</h2>
      <CreditLine credits={credits} unlimited={unlimited} />

      <div className="grid gap-3 sm:grid-cols-3">
        <label className="block text-sm">
          <span className="font-medium">{t("Subjek")}</span>
          <select value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1 w-full rounded-md border border-surface-line p-2">
            <option value="">{t("Pilih subjek")}</option>
            {subjects.map(([slug, name]) => <option key={slug} value={slug}>{t(name)}</option>)}
          </select>
        </label>
        <label className="block text-sm">
          <span className="font-medium">{t("Tahun / Tingkatan")}</span>
          <select value={levelYear} onChange={(e) => setLevelYear(e.target.value)} disabled={!subject} className="mt-1 w-full rounded-md border border-surface-line p-2">
            {levels.map(([k, name]) => <option key={k} value={k}>{name}</option>)}
          </select>
        </label>
        <label className="block text-sm">
          <span className="font-medium">{t("Versi")}</span>
          <select value={versionId} onChange={(e) => setVersionId(e.target.value)} disabled={variants.length < 2} className="mt-1 w-full rounded-md border border-surface-line p-2">
            {variants.map((v) => <option key={v.versionId} value={v.versionId}>{variantName(v)}</option>)}
          </select>
        </label>
      </div>

      {standards.length > 0 && (
        <fieldset>
          <legend className="text-sm font-medium">
            {t("Standard Pembelajaran ({n}/{max} dipilih)", { n: picked.length, max: PRACTICE_LIMITS.MAX_STANDARDS })}
          </legend>
          <div className="mt-2 max-h-96 space-y-3 overflow-y-auto pr-1">
            {groups.map(([topic, list]) => {
              const codes = list.map((s) => s.spCode);
              const all = codes.every((c) => picked.includes(c));
              return (
                <div key={topic} className="rounded-lg border border-surface-line p-2">
                  <button type="button" onClick={() => toggleGroup(codes)} className="text-left text-sm font-semibold text-brand underline-offset-2 hover:underline">
                    {all ? "☑" : "☐"} {topic}
                  </button>
                  <ul className="mt-1 space-y-1">
                    {list.map((s) => (
                      <li key={s.spCode}>
                        <label className="flex gap-2 text-xs">
                          <input type="checkbox" checked={picked.includes(s.spCode)} disabled={!picked.includes(s.spCode) && full} onChange={() => toggle(s.spCode)} />
                          <span><strong>{s.spCode}</strong> {s.spDesc}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </fieldset>
      )}

      <label className="block text-sm">
        <span className="font-medium">{t("Bilangan soalan: {n}", { n: count })}</span>
        <input type="range" min={PRACTICE_LIMITS.MIN_QUESTIONS} max={PRACTICE_LIMITS.MAX_QUESTIONS} value={count} onChange={(e) => setCount(Number(e.target.value))} className="mt-1 w-full" />
        <span className="text-xs text-muted">{t("Susunan bahagian mengikut subjek · aras rendah:sederhana:tinggi = 5:3:2")}</span>
      </label>

      {langs.length > 1 && (
        <fieldset>
          <legend className="text-sm font-medium">{t("Bahasa kertas")}</legend>
          <div className="mt-1 flex flex-wrap gap-2">
            {langs.map((l) => (
              <button key={l} type="button" aria-pressed={language === l} onClick={() => setLanguage(l)}
                className={`rounded-md px-3 py-1 text-sm ${language === l ? "bg-accent text-white" : "bg-surface-muted"}`}>
                {LANG_LABEL[l]}
              </button>
            ))}
          </div>
        </fieldset>
      )}

      {error && <Alert kind="error">{error}</Alert>}
      {notice && <Alert kind="success">{notice}</Alert>}
      <Button type="submit" busy={busy} disabled={!versionId || picked.length === 0 || (!unlimited && (credits ?? 0) < 1)} className="w-full">
        {t("Jana kertas")}
      </Button>
      {busy && <p className="text-xs text-muted" role="status">{t("AI sedang menulis dan menyemak soalan. Ini boleh ambil sehingga 1–2 minit.")}</p>}
      <p className="text-xs text-muted">{t("Kod dan huraian SP diambil terus daripada dokumen rasmi DSKP.")}</p>
    </form>
  );
}

